import { useForm } from '@inertiajs/react'
import { easeInOut, motion } from 'motion/react'
import { Calendar, Clock, FileCheck, MessageSquare, Users, Zap } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { urlFor } from '~/core/tuyau'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const benefits = [
  {
    icon: Clock,
    title: 'Save 50+ Hours Per Review',
    description: 'Automated data extraction and analysis cuts review time dramatically',
  },
  {
    icon: FileCheck,
    title: 'MDR Compliant',
    description: 'Built-in compliance checks and documentation for regulatory requirements',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Seamless workflow for your entire regulatory team',
  },
  {
    icon: MessageSquare,
    title: 'Dedicated Support',
    description: '1-on-1 support from our expert team',
  },
  {
    icon: Calendar,
    title: 'Fast Implementation',
    description: 'Get up and running in days, not months',
  },
  {
    icon: Zap,
    title: 'Custom Integration',
    description: 'Tailored to your existing workflows and SOPs',
  },
]

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-12, 12, -12],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const Index = () => {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    email: '',
    company: '',
  })

  const handleSubmit: React.ComponentProps<'form'>['onSubmit'] = (e) => {
    e.preventDefault()
    post(urlFor('website.demo-requests'))
  }

  return (
    <>
      <Seo
        title="Pricing | SOTA Navigator"
        description="What SOTA Navigator costs, and what is included at each plan, for teams running literature reviews for medical device clinical evaluations under EU MDR."
      />

      <PageLayout>
        <div className="relative overflow-hidden bg-[#001871] text-white">
          {/* Enhanced texture overlay */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
          <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

          {/* Abstract shapes with improved contrast */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.15, scale: 1 }}
            transition={{ duration: 2 }}
            className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.15, scale: 1 }}
            transition={{ duration: 2, delay: 0.5 }}
            className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
          />

          {/* Floating elements */}
          <motion.div
            variants={floatingAnimation}
            initial="initial"
            animate="animate"
            className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
          />
          <motion.div
            variants={floatingAnimation}
            initial="initial"
            animate="animate"
            transition={{ delay: 1 }}
            className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
          />

          <div className="relative py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-4xl text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className="space-y-8"
                >
                  <motion.div
                    variants={glowAnimation}
                    initial="initial"
                    animate="animate"
                    className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                  >
                    Transform Your Literature Review Process
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                    className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                  >
                    Schedule Your Personalized Demo
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                    className="mx-auto max-w-3xl text-xl text-white/80"
                  >
                    See how SOTA Navigator can help your team save time while ensuring compliance
                  </motion.p>
                </motion.div>
              </div>

              <div className="mt-24 grid grid-cols-1 gap-16 lg:grid-cols-2">
                {/* Benefits */}
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex flex-col items-start rounded-xl bg-white/10 p-6 shadow-lg backdrop-blur-xs"
                    >
                      <div className="mb-4 rounded-xl bg-white/20 p-3">
                        <benefit.icon className="size-6 text-white" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-white">{benefit.title}</h3>
                      <p className="text-sm text-white/70">{benefit.description}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Book a Call CTA */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="relative overflow-hidden rounded-2xl bg-white p-8 text-[#001871]"
                >
                  <div className="relative z-10">
                    <h2 className="mb-6 text-3xl font-bold">Book Your Demo Call</h2>
                    <p className="mb-8 text-gray-600">
                      See how SOTA Navigator can transform your literature review process with a
                      personalized demo tailored to your team's needs.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-medium">Name</label>
                        <input
                          type="text"
                          value={data.name}
                          onChange={(e) => setData('name', e.target.value)}
                          required
                          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 placeholder:text-gray-500"
                          placeholder="Your name"
                        />
                        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium">Email</label>
                        <input
                          type="email"
                          value={data.email}
                          onChange={(e) => setData('email', e.target.value)}
                          required
                          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 placeholder:text-gray-500"
                          placeholder="your@email.com"
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                        )}
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium">Company</label>
                        <input
                          type="text"
                          value={data.company}
                          onChange={(e) => setData('company', e.target.value)}
                          required
                          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 placeholder:text-gray-500"
                          placeholder="Your company"
                        />
                        {errors.company && (
                          <p className="mt-1 text-sm text-red-500">{errors.company}</p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={processing}
                        className="w-full rounded-xl bg-[#001871] px-6 py-3 font-medium text-white transition-colors hover:bg-[#001871]/90 disabled:opacity-50"
                      >
                        {processing ? 'Submitting...' : 'Schedule Your Demo'}
                      </button>
                    </form>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  )
}

export default Index
